/**
 * CreditsAdminSection — Admin Portal "{creditLabel}" tab (Phase 3.0 redesign).
 *
 * Was: per-user search + grant form (Phase 2.6).
 * Now: **global, read-only ledger feed** sourced from `GET /api/admin/credit-ledger`.
 *      Grants now live on the Accounts detail panel — this tab is the
 *      historical audit feed across every account.
 *
 * Filters compose with AND (server-side):
 *   - email substring (>= 2 chars, debounced 250ms)
 *   - sign: all / positive (grants) / negative (debits)
 *   - sort: created_at (default) or amount, asc/desc
 *   - pagination: 25/page, server-paginated
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { cn } from "@/lib/utils";
import {
  Loader2,
  Search,
  Coins,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

const PAGE_SIZE = 25;

function formatTs(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 16).replace("T", " ");
}

function SortHeader({ field, current, order, onClick, children, className }) {
  const isActive = current === field;
  return (
    <th className={cn("py-2 pr-3 text-left", className)}>
      <button
        type="button"
        onClick={() => onClick(field)}
        className={cn(
          "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors",
          isActive ? "text-pp-text" : "text-pp-text-muted hover:text-pp-text"
        )}
        data-testid={`credit-ledger-sort-${field}`}
      >
        {children}
        {isActive &&
          (order === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          ))}
      </button>
    </th>
  );
}

export default function CreditsAdminSection() {
  const { creditLabel } = useConfig();

  // --- filter state --------------------------------------------------------
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const debounceRef = useRef(null);

  const [sign, setSign] = useState("all");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });

  // Debounce email substring search → reset to page 1 on every commit.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, sort, order };
      if (debouncedQ.length >= 2) params.q = debouncedQ;
      if (sign !== "all") params.sign = sign;
      const { data: body } = await api.get("/admin/credit-ledger", { params });
      setData(body);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
    // `fetchPage` closes over the deps below + setData/setLoading
    // (stable setters); adding it re-fires the effect after every fetch
    // (pagination anti-loop). Same shape as the AdminDashboardPage:309
    // and AccountsManagementSection:698 patterns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, sign, sort, order, page]);

  const toggleSort = (field) => {
    if (sort === field) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setOrder("desc");
    }
    setPage(1);
  };

  // --- summary metrics (current page only — server already filtered) -------
  const pageTotals = useMemo(() => {
    let credit = 0;
    let debit = 0;
    for (const e of data.items) {
      if (e.amount >= 0) credit += e.amount;
      else debit += e.amount;
    }
    return { credit, debit };
  }, [data.items]);

  return (
    <section className="mt-2" data-testid="credit-ledger-section">
      <header className="mb-5">
        <div className="flex items-center gap-2">
          <Coins className="h-6 w-6 text-brand-orange" aria-hidden="true" />
          <h2 className="font-heading text-2xl font-semibold text-pp-text">
            {creditLabel}
          </h2>
        </div>
        <p className="mt-1 text-sm text-pp-text-muted">
          Global ledger of every {creditLabel} grant and adjustment across the
          platform. To issue new {creditLabel}, open an account from the{" "}
          <strong className="text-pp-text">Accounts</strong> tab.
        </p>
      </header>

      {/* --- Filter bar -------------------------------------------------- */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Search by email
          </Label>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-pp-text-muted" />
            <Input
              type="text"
              placeholder="e.g. customer@…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-11 rounded-full pl-12 pr-5"
              data-testid="credit-ledger-search-input"
            />
          </div>
        </div>
        <div className="w-[180px]">
          <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
            Sign
          </Label>
          <Select
            value={sign}
            onValueChange={(v) => {
              setSign(v);
              setPage(1);
            }}
          >
            <SelectTrigger
              className="mt-2 h-11 rounded-full"
              data-testid="credit-ledger-sign-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="positive">Grants (+)</SelectItem>
              <SelectItem value="negative">Debits (−)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* --- Page totals strip ------------------------------------------ */}
      {data.items.length > 0 && (
        <div
          className="mb-3 flex flex-wrap gap-2 text-xs text-pp-text-muted"
          data-testid="credit-ledger-totals"
        >
          <span className="rounded-full bg-pp-surface2 px-3 py-1">
            Page grants: <strong className="text-brand-orange">+${pageTotals.credit.toFixed(2)}</strong>
          </span>
          <span className="rounded-full bg-pp-surface2 px-3 py-1">
            Page debits: <strong className="text-slate-600 dark:text-slate-300">−${Math.abs(pageTotals.debit).toFixed(2)}</strong>
          </span>
        </div>
      )}

      {/* --- Table ------------------------------------------------------- */}
      <Card
        className="rounded-2xl border border-pp-border bg-pp-surface p-0 overflow-hidden"
        style={{ boxShadow: "var(--pp-card-shadow)" }}
        data-testid="credit-ledger-card"
      >
        {loading ? (
          <p className="flex items-center gap-2 p-6 text-sm text-pp-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading ledger…
          </p>
        ) : data.items.length === 0 ? (
          <p
            className="p-10 text-center text-sm text-pp-text-muted"
            data-testid="credit-ledger-empty"
          >
            No entries match those filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="credit-ledger-table">
              <thead className="border-b border-pp-border bg-pp-surface2">
                <tr>
                  <SortHeader
                    field="created_at"
                    current={sort}
                    order={order}
                    onClick={toggleSort}
                    className="pl-5"
                  >
                    Date
                  </SortHeader>
                  <SortHeader field="amount" current={sort} order={order} onClick={toggleSort}>
                    Amount
                  </SortHeader>
                  <th className="py-2 pr-3 text-left text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                    Account
                  </th>
                  <th className="py-2 pr-3 text-left text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                    Reason
                  </th>
                  <th className="py-2 pr-5 text-left text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                    Admin
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e) => {
                  const positive = e.amount >= 0;
                  return (
                    <tr
                      key={e.id}
                      className="border-t border-pp-border align-top"
                      data-testid={`credit-ledger-row-${e.id}`}
                    >
                      <td className="py-3 pl-5 pr-3 text-pp-text-muted">
                        {formatTs(e.created_at)}
                      </td>
                      <td
                        className={cn(
                          "py-3 pr-3 font-mono tabular-nums font-semibold",
                          positive ? "text-brand-orange" : "text-slate-500 dark:text-slate-400"
                        )}
                      >
                        {positive ? "+" : "−"}${Math.abs(e.amount).toFixed(2)}
                      </td>
                      <td className="py-3 pr-3 text-pp-text">
                        {e.user_email}
                        <span className="ml-1 text-[10px] uppercase tracking-wider text-pp-text-muted">
                          {e.user_role}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-pp-text">{e.reason}</td>
                      <td className="py-3 pr-5 text-pp-text-muted">{e.admin_email}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* --- Pagination -------------------------------------------------- */}
      {data.total > 0 && (
        <div
          className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-pp-text-muted"
          data-testid="credit-ledger-pagination"
        >
          <span>
            Showing page <strong className="text-pp-text">{data.page}</strong> of{" "}
            <strong className="text-pp-text">{data.pages}</strong> ·{" "}
            <strong className="text-pp-text">{data.total}</strong> total
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-9 rounded-full"
              data-testid="credit-ledger-page-prev"
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={page >= data.pages || loading}
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              className="h-9 rounded-full"
              data-testid="credit-ledger-page-next"
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
